"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { controlTypography, focusRing } from "./control-styles";
import { Toggle as TogglePrimitive } from "radix-ui";

const toggleVariants = cva(
  `${controlTypography} relative inline-flex min-w-max flex-1 basis-0 items-center justify-center gap-2 text-center tracking-normal break-normal whitespace-normal transition-colors duration-180 hover:text-foreground ${focusRing} disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=active]:border-edge data-[state=active]:bg-secondary data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=on]:border-edge data-[state=on]:bg-secondary data-[state=on]:text-foreground data-[state=on]:shadow-sm dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
  {
    variants: {
      variant: {
        default: "border border-transparent bg-transparent text-muted-foreground",
        outline:
          "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        // Content-height sizes: py gives up the 1px border so heights stay 32/36/40px.
        default: "h-auto min-h-9 rounded-lg px-4 py-[calc(--spacing(2)-1px)]",
        sm: "h-auto min-h-8 rounded-md px-1.5 py-[calc(--spacing(1.5)-1px)]",
        lg: "h-auto min-h-10 rounded-xl px-6 py-[calc(--spacing(2)-1px)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Toggle({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- ShadCN exposes the variant helper alongside its component for shared styling; edits may reload importers.
export { Toggle, toggleVariants };
